import React from "react";
import { UseFormReturn } from "react-hook-form";

import {
  Button,
  Card,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components";
import { RequiredLabel } from "@/components/ui/required-label";
import { useI18n } from "@/context/i18n";
import { formatCpfCnpj } from "@/core/utils";

import { TInsuranceCompaniesFormData } from "../insurance-companies.schema";
import { getInsuranceCompanyTypeOptions } from "../insurance-companies.type";

export interface IInsuranceCompaniesFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<TInsuranceCompaniesFormData>;
  onSubmit: (values: TInsuranceCompaniesFormData) => void;
}
export const InsuranceCompaniesForm: React.FC<IInsuranceCompaniesFormProps> = ({
  open,
  onOpenChange,
  form,
  onSubmit,
}) => {
  const { t } = useI18n();

  const typeOptions = getInsuranceCompanyTypeOptions(t);

  if (open) {
    return (
      <Card className="p-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required htmlFor="type">
                      {t("insuranceCompanies.form.fields.type")}
                    </RequiredLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              "insuranceCompanies.form.fields.type"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {typeOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required htmlFor="cnpj">
                      {t("insuranceCompanies.form.fields.cnpj")}
                    </RequiredLabel>
                    <FormControl>
                      <Input
                        {...field}
                        id="cnpj"
                        placeholder=" "
                        onChange={(e) => {
                          const { value } = e.target;
                          e.target.value = formatCpfCnpj(value);
                          field.onChange(e);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required htmlFor="name">
                      {t("clients.form.fields.name")}
                    </RequiredLabel>
                    <FormControl>
                      <Input {...field} id="name" placeholder=" " />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="corporate_name"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel required htmlFor="corporate_name">
                      {t("clients.form.fields.corporate_name")}
                    </RequiredLabel>
                    <FormControl>
                      <Input {...field} id="corporate_name" placeholder=" " />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                {t("generic.cancel")}
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {t("generic.register")}
              </Button>
            </div>
          </form>
        </Form>
      </Card>
    );
  }
};
